'use client';

import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  PinterestIcon,
  PinterestShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share';
import { BrandConfig } from 'types/brand-config';

import Button from '@/components/ui/button';

import Alert from './alert';

interface ShareProps {
  brand: BrandConfig;
  title?: string;
  className?: string;
  subject: string;
  options?: string[];
  media: string;
  linkTitle?: string;
}
const Share: React.FC<ShareProps> = ({
  brand,
  title = 'Share',
  subject,
  className,
  options,
  media,
  linkTitle,
}) => {
  const path = usePathname();
  const search = useSearchParams();
  const url = search.toString()
    ? `https://${brand.canonicalHostname || brand.domain}${path}?${search.toString()}`
    : `https://${brand.canonicalHostname || brand.domain}${path}`;

  const [currentUrl, setCurrentUrl] = useState('');
  const [canCopy, setCanCopy] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Set the current URL only on the client side
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
    if (typeof navigator !== 'undefined') {
      setCanCopy(!!navigator.clipboard);
      setCanShare(!!navigator.share);
    }
  }, []);

  const handleCopyUrl = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);
    if (!navigator.clipboard) {
      setErrorMessage(
        'Clipboard API is not supported in your browser. Please copy the URL manually.'
      );
      return;
    }

    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        setSuccessMessage('Current URL copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy URL:', err);
        setErrorMessage('Could not copy the URL. Please try again.');
      });
  };

  if (!options) {
    return (
      <div className={className}>
        <EmailShareButton
          aria-label="Share in an email"
          url={url}
          subject={subject}
        >
          <span className="caps-lg flex h-[5rem] w-[8rem] items-center justify-between px-[1rem] text-[0.875rem] text-primary-main">
            {title}
            <svg
              className="h-[1.3125rem] w-[1.4375rem]"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="21"
              viewBox="0 0 24 21"
              fill="none"
            >
              <path
                d="M0.333061 20.3954C0.33116 20.5401 0.380184 20.6806 0.471239 20.7915C0.562294 20.9024 0.689346 20.9764 0.829336 21H0.938274C1.05813 21.001 1.17549 20.9651 1.27488 20.8968C1.37428 20.8285 1.45104 20.7311 1.49507 20.6175C4.21853 13.6839 11.5174 13.6099 11.8321 13.6099H13.6478V17.928C13.6484 18.0498 13.6843 18.1686 13.7511 18.2696C13.8179 18.3706 13.9125 18.4492 14.023 18.4955C14.1324 18.544 14.2539 18.5571 14.3708 18.5328C14.4878 18.5085 14.5945 18.4481 14.6766 18.3598L23.1496 9.72362C23.2075 9.66824 23.2536 9.60135 23.2852 9.52708C23.3167 9.45281 23.333 9.37274 23.333 9.29181C23.333 9.21089 23.3167 9.13082 23.2852 9.05655C23.2536 8.98228 23.2075 8.91539 23.1496 8.86001L14.6766 0.223849C14.6016 0.125554 14.4975 0.0544836 14.3798 0.0211545C14.2621 -0.0121747 14.137 -0.00599178 14.023 0.0387882C13.9045 0.0879855 13.8044 0.174189 13.7369 0.285021C13.6695 0.395852 13.6383 0.525608 13.6478 0.655657V4.97374C10.3538 4.94198 7.15972 6.12505 4.65428 8.30482C1.78557 10.9574 0.333061 15.0287 0.333061 20.3954Z"
                fill="#2D446B"
              />
            </svg>
          </span>
        </EmailShareButton>
      </div>
    );
  }
  return (
    <div className={className}>
      {successMessage && (
        <div className="fixed bottom-10 left-1/2 z-10 -translate-x-1/2">
          <Alert
            message={successMessage}
            type="info"
            close={() => {
              setSuccessMessage(null);
            }}
            timeout={2000}
          />
        </div>
      )}
      {errorMessage && (
        <div className="fixed bottom-10 left-1/2 z-10 -translate-x-1/2">
          <Alert
            message={errorMessage}
            type="warning"
            close={() => {
              setErrorMessage(null);
            }}
            timeout={5000}
          />
        </div>
      )}
      <div className="relative">
        <Menu>
          <MenuButton as={Button} style="text" type="button" aria-label={title}>
            <span className="caps-lg flex h-[5rem] w-[8rem] items-center justify-between px-[1rem] text-[0.875rem] text-primary-main">
              {title}
              <svg
                className="h-[1.3125rem] w-[1.4375rem]"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="21"
                viewBox="0 0 24 21"
                fill="none"
              >
                <path
                  d="M0.333061 20.3954C0.33116 20.5401 0.380184 20.6806 0.471239 20.7915C0.562294 20.9024 0.689346 20.9764 0.829336 21H0.938274C1.05813 21.001 1.17549 20.9651 1.27488 20.8968C1.37428 20.8285 1.45104 20.7311 1.49507 20.6175C4.21853 13.6839 11.5174 13.6099 11.8321 13.6099H13.6478V17.928C13.6484 18.0498 13.6843 18.1686 13.7511 18.2696C13.8179 18.3706 13.9125 18.4492 14.023 18.4955C14.1324 18.544 14.2539 18.5571 14.3708 18.5328C14.4878 18.5085 14.5945 18.4481 14.6766 18.3598L23.1496 9.72362C23.2075 9.66824 23.2536 9.60135 23.2852 9.52708C23.3167 9.45281 23.333 9.37274 23.333 9.29181C23.333 9.21089 23.3167 9.13082 23.2852 9.05655C23.2536 8.98228 23.2075 8.91539 23.1496 8.86001L14.6766 0.223849C14.6016 0.125554 14.4975 0.0544836 14.3798 0.0211545C14.2621 -0.0121747 14.137 -0.00599178 14.023 0.0387882C13.9045 0.0879855 13.8044 0.174189 13.7369 0.285021C13.6695 0.395852 13.6383 0.525608 13.6478 0.655657V4.97374C10.3538 4.94198 7.15972 6.12505 4.65428 8.30482C1.78557 10.9574 0.333061 15.0287 0.333061 20.3954Z"
                  fill="currentColor"
                />
              </svg>
            </span>
          </MenuButton>
          <MenuItems
            transition
            anchor="bottom start"
            className="border-1 flex origin-bottom-left flex-col rounded-xl border bg-white px-1 shadow-sm transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[open]:z-[2] data-[open]:-translate-x-[1rem] data-[open]:-translate-y-[1rem] data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            {options!.includes('copy') && canCopy && (
              <MenuItem as="div" className="group">
                <button
                  aria-label="Copy link to clipboard"
                  onClick={handleCopyUrl}
                  type="button"
                  className="flex h-[3.5rem] items-center justify-start border-b px-4 text-gray-7 group-hover:text-black-2"
                >
                  <div className="border-1 rounded-full border p-[.65rem]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1em"
                      height="1em"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M19 21H8V7h11m0-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m-3-4H4a2 2 0 0 0-2 2v14h2V3h12z"
                      />
                    </svg>
                  </div>
                  <h6 className="ut-sm whitespace-nowrap text-black-1">
                    Copy Link
                  </h6>
                </button>
              </MenuItem>
            )}
            {options!.includes('email') && (
              <MenuItem>
                <div className="flex h-[3.5rem] items-center justify-start border-b px-4">
                  <EmailShareButton
                    aria-label="Share in an email"
                    url={url}
                    subject={subject}
                  >
                    <EmailIcon round className="h-9 w-9" />{' '}
                    <h6 className="ut-sm text-black-1">Email</h6>
                  </EmailShareButton>
                </div>
              </MenuItem>
            )}
            {options!.includes('facebook') && (
              <MenuItem>
                <div className="flex h-[3.5rem] items-center justify-start border-b px-4">
                  <FacebookShareButton
                    aria-label="Share on Facebook"
                    url={url}
                    hashtag=""
                    title={linkTitle}
                  >
                    <FacebookIcon round className="h-9 w-9" />{' '}
                    <h6 className="ut-sm text-black-1">Facebook</h6>
                  </FacebookShareButton>
                </div>
              </MenuItem>
            )}
            {options!.includes('pinterest') && (
              <MenuItem>
                <div className="flex h-[3.5rem] items-center justify-start border-b px-4">
                  <PinterestShareButton
                    aria-label="Share on Pinterest"
                    media={media}
                    url={url}
                    title={linkTitle}
                  >
                    <PinterestIcon round className="h-9 w-9" />{' '}
                    <h6 className="ut-sm text-black-1">Pinterest</h6>
                  </PinterestShareButton>
                </div>
              </MenuItem>
            )}
            {options!.includes('twitter') && (
              <MenuItem>
                <div className="flex h-[3.5rem] items-center justify-start border-b px-4">
                  <TwitterShareButton
                    aria-label="Share on Twitter"
                    url={url}
                    title={linkTitle}
                  >
                    <TwitterIcon round className="h-9 w-9" />{' '}
                    <h6 className="ut-sm text-black-1">Twitter</h6>
                  </TwitterShareButton>
                </div>
              </MenuItem>
            )}
            {options!.includes('linkedin') && (
              <MenuItem>
                <div className="flex h-[3.5rem] items-center justify-start border-b px-4">
                  <LinkedinShareButton
                    aria-label="Share on Linkedin"
                    url={url}
                    title={linkTitle}
                  >
                    <LinkedinIcon round className="h-9 w-9" />{' '}
                    <h6 className="ut-sm text-black-1">LinkedIn</h6>
                  </LinkedinShareButton>
                </div>
              </MenuItem>
            )}
            {options!.includes('more') && canShare && (
              <MenuItem>
                <button
                  aria-label="More share options"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // This navigator share has limited browser support, and a known issue.
                    //
                    // Sentry: https://maui-resort-rentals.sentry.io/issues/6230299190/?environment=production&project=4506995742736384&query=navigator&referrer=issue-stream&statsPeriod=90d&stream_index=0
                    //    Bug: https://issues.chromium.org/issues/40874578
                    //    Fix: https://stackoverflow.com/questions/64055853/navigator-share-only-working-once-in-ios-second-click-throws-error-request-is
                    navigator
                      .share({ url, title })
                      .then(() => {})
                      .catch(() => {
                        // no action, just keep these out of Sentry
                        console.log('Share canceled');
                      });
                  }}
                  type="button"
                  className="group flex h-[3.5rem] items-center justify-start border-b px-4 text-white"
                >
                  <div className="border-1 rounded-full border bg-gray-3 p-[.65rem] group-hover:bg-gray-6">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1em"
                      height="1em"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M16 12a2 2 0 0 1 2-2a2 2 0 0 1 2 2a2 2 0 0 1-2 2a2 2 0 0 1-2-2m-6 0a2 2 0 0 1 2-2a2 2 0 0 1 2 2a2 2 0 0 1-2 2a2 2 0 0 1-2-2m-6 0a2 2 0 0 1 2-2a2 2 0 0 1 2 2a2 2 0 0 1-2 2a2 2 0 0 1-2-2"
                      />
                    </svg>
                  </div>
                  <h6 className="ut-sm whitespace-nowrap text-black-1">
                    More Options
                  </h6>
                </button>
              </MenuItem>
            )}
          </MenuItems>
        </Menu>
      </div>
    </div>
  );
};

export default Share;
